import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { T } from "@/components/T";

const faqsData = {
  de: [
    { q: "Welche Holzbauprojekte realisiert Holzbuddy?", a: "Holzbuddy realisiert Aufstockungen und Anbauten, Dachstühle und Holztragwerke, Holzhäuser, Holzfassaden, Hotelbau in Holz, Sanierungen im Bestand, Hallen- und Gewerbebauten sowie Terrassen und Balkone." },
    { q: "Arbeitet Holzbuddy nur in Meran oder in ganz Südtirol?", a: "Holzbuddy ist in Meran ansässig und aktiv in der gesamten Region Südtirol / Alto Adige tätig. Auch Projekte in der weiteren Umgebung sind nach Vereinbarung möglich." },
    { q: "Macht Holzbuddy auch Aufstockungen auf bestehenden Gebäuden?", a: "Ja – Aufstockungen sind eine unserer Kernkompetenzen. Holz ist das ideale Material: leicht, schnell montiert und statisch gut integrierbar." },
    { q: "Kann Holzbuddy Hotel- und Tourismusprojekte in Holz umsetzen?", a: "Ja. Wir kennen die besonderen Anforderungen der Hotellerie: kurze Umbauzeiten, laufender Betrieb, hohe Qualität. Durch Vorfertigung können wir Bauphasen stark verkürzen." },
    { q: "Wie läuft eine erste Anfrage bei Holzbuddy ab?", a: "Sie schreiben uns kurz, was Sie vorhaben – oder rufen einfach an. Das Holzbuddy Team vereinbart ein erstes Gespräch und gibt Ihnen eine ehrliche Einschätzung. Alles ohne Verpflichtung." },
    { q: "In welchen Sprachen betreut Holzbuddy seine Kunden?", a: "Wir betreuen unsere Kunden in Deutsch und Italienisch. Als Südtiroler Unternehmen ist zweisprachige Betreuung für uns selbstverständlich." },
  ],
  it: [
    { q: "Quali progetti di costruzione in legno realizza Holzbuddy?", a: "Holzbuddy realizza sopraelevazioni e ampliamenti, strutture del tetto e portanti in legno, case in legno, facciate in legno, edilizia alberghiera, risanamenti nel patrimonio edilizio, capannoni industriali e commerciali, terrazze e balconi." },
    { q: "Holzbuddy lavora solo a Merano o in tutto l'Alto Adige?", a: "Holzbuddy ha sede a Merano ed è attivo in tutta la regione Alto Adige / Südtirol. Anche i progetti nelle zone limitrofe sono possibili previo accordo." },
    { q: "Holzbuddy realizza anche sopraelevazioni su edifici esistenti?", a: "Sì – le sopraelevazioni sono una delle nostre competenze principali. Il legno è il materiale ideale: leggero, montato rapidamente e facilmente integrabile dal punto di vista statico." },
    { q: "Holzbuddy può realizzare progetti alberghieri e turistici in legno?", a: "Sì. Conosciamo le particolari esigenze dell'industria alberghiera: brevi tempi di ristrutturazione, esercizio continuo, alta qualità. Grazie alla prefabbricazione possiamo ridurre notevolmente le fasi costruttive." },
    { q: "Come funziona una prima richiesta a Holzbuddy?", a: "Basta scriverci brevemente cosa avete in mente – o semplicemente chiamarci. Il team Holzbuddy fissa un primo colloquio e vi dà una valutazione onesta. Tutto senza impegno." },
    { q: "In quali lingue Holzbuddy assiste i propri clienti?", a: "Assistiamo i nostri clienti in tedesco e italiano. Come azienda altoatesina, l'assistenza bilingue è per noi una cosa ovvia." },
  ],
};

function FaqItem({ q, a, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen || false);
  return (
    <div className="border-b border-hb-border">
      <button className="w-full flex items-center justify-between py-5 text-left group"
        onClick={() => setOpen(!open)} data-testid="faq-toggle" aria-expanded={open}>
        <span className="text-sm md:text-base font-semibold text-hb-dark font-manrope pr-4">{q}</span>
        <ChevronDown size={15} className={`text-hb-muted flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180 text-hb-yellow" : ""}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? "max-h-72 pb-5" : "max-h-0"}`}>
        <p className="text-sm text-hb-text font-inter leading-relaxed">{a}</p>
      </div>
    </div>
  );
}

export function FaqSection() {
  const { lang } = useLanguage();
  const faqs = faqsData[lang];
  return (
    <section data-testid="faq-section" className="section-gap bg-white">
      <div className="container-hb">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div className="lg:sticky lg:top-28">
            <span className="label-text block mb-5 reveal">FAQ</span>
            <h2 className="font-bebas text-hb-dark mb-5 reveal reveal-delay-1" style={{ fontSize: "clamp(48px, 6vw, 80px)", lineHeight: 1 }}>
              <T de={<>Häufige<br />Fragen.</>} it={<>Domande<br />Frequenti.</>} />
            </h2>
            <p className="section-subtitle mb-8 max-w-xs reveal reveal-delay-2">
              <T de="Nicht gefunden, was Sie suchen? Schreiben Sie uns direkt."
                 it="Non hai trovato quello che cerchi? Scrivici direttamente." />
            </p>
            <Link to="/kontakt" data-testid="faq-cta-btn" className="btn-primary reveal reveal-delay-3">
              <T de="Frage stellen" it="Fai una domanda" />
            </Link>
          </div>
          <div data-testid="faq-list">
            {faqs.map((item, i) => (
              <FaqItem key={i} q={item.q} a={item.a} defaultOpen={i === 0} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
